import { PageHeader } from "@portal/components/layout/PageHeader";
import { getDefaultTab, Tab, TabBar } from "@portal/components/ui/TabBar";
import { useAuthenticatedUser } from "@portal/providers/AuthenticatedUserProvider";
import { type ReactNode, useCallback, useState } from "react";
import { NonEmptyArray } from "../../../shared/types";
import { AdminUserMenu } from "../../admin/settings/AdminUserMenu";
import { AdminHeaderActionButtons } from "../../admin/shared/AdminHeaderActionButtons";
import { AgentUserMenu } from "../../agent/settings/AgentUserMenu";
import { AgentHeaderActionButtons } from "../../agent/shared/AgentHeaderActionButtons";
import { ScopeSelector } from "../ScopeSelector";
import { type EntityScope, isEntityScope } from "../scope";

export type PreviewTableProps = {
	searchString: string;
	scope?: EntityScope;
};

export type EntityPreviewsLayoutProps = {
	title: string;
	storageKey: string;
	tabs: NonEmptyArray<Tab<PreviewTableProps>>;
	/** When true, the layout owns a Me/Everyone scope selector that drives the metrics row and the table data. */
	enableScope?: boolean;
	/**
	 * Optional metrics row rendered above the tabs. Receives the current scope, which is
	 * fixed at `defaultScope` unless `enableScope` exposes the selector. Rendered independently
	 * of `enableScope` so metrics can be shown with a fixed scope and no visible selector.
	 */
	renderMetrics?: (scope: EntityScope) => ReactNode;
	defaultScope?: EntityScope;
};

export const EntityPreviewsLayout = ({
	title,
	storageKey,
	tabs,
	enableScope = false,
	renderMetrics,
	defaultScope = "me",
}: EntityPreviewsLayoutProps) => {
	const scopeStorageKey = `${storageKey}_scope`;
	const [scope, setScope] = useState<EntityScope>(() => {
		try {
			const stored = sessionStorage.getItem(scopeStorageKey);
			return isEntityScope(stored) ? stored : defaultScope;
		} catch {
			return defaultScope;
		}
	});
	const handleScopeChange = useCallback(
		(next: EntityScope) => {
			setScope(next);
			try {
				sessionStorage.setItem(scopeStorageKey, next);
			} catch {}
		},
		[scopeStorageKey],
	);
	const [selectedTab, setSelectedTab] = useState(getDefaultTab(tabs));
	const [searchString, setSearchString] = useState<string>(() => {
		try {
			return sessionStorage.getItem(storageKey) ?? "";
		} catch {
			return "";
		}
	});
	const { user } = useAuthenticatedUser();

	const isAdmin = user.type === "admin";

	const handleTabChange = useCallback(
		(tabId: string) => {
			const newTab = tabs.find((tab) => tab.id === tabId);

			if (newTab) {
				setSelectedTab(newTab);
				setSearchString("");
				sessionStorage.setItem(storageKey, "");

				newTab.onSelect && newTab.onSelect();
			}
		},
		[tabs, storageKey],
	);

	const TabContent = selectedTab.Component;

	return (
		<div className="flex flex-col w-full h-full pb-8 gap-3">
			<PageHeader
				className="shrink-0"
				title={title}
				testId="entity-previews-layout"
				userMenu={isAdmin ? <AdminUserMenu /> : <AgentUserMenu />}
				actionButtons={isAdmin ? <AdminHeaderActionButtons /> : <AgentHeaderActionButtons />}
			/>
			{renderMetrics ? <div className="px-8 shrink-0">{renderMetrics(scope)}</div> : null}
			<div className="flex items-center space-x-[25px] px-8 shrink-0">
				<TabBar options={tabs} activeTabId={selectedTab.id} onTabChange={handleTabChange} />
				{enableScope ? (
					<ScopeSelector
						value={scope}
						onChange={handleScopeChange}
						testId="entity-previews-scope-selector"
					/>
				) : null}
			</div>
			<div className="flex-grow min-h-0 px-8">
				{TabContent ? (
					<TabContent searchString={searchString} scope={enableScope ? scope : undefined} />
				) : null}
			</div>
		</div>
	);
};
